/**
 * Machines — every machine the organization's members have linked, and the
 * verb that cuts one off (`org-machines.ts`).
 *
 * Two lists, because a member's machine holds two credentials and an owner is
 * asking two questions. **Linked machines** are device tokens: what reads this
 * organization's workspaces and receives their secrets. **Seat licences** are
 * the machines each seat's key is running on, as the gate counts them.
 *
 * What *Cut off* does is said in the dialog, in full, because the obvious
 * reading is wrong in one place: it does **not** sign the machine out of the
 * member's other organizations — the token is theirs and keeps working there,
 * and so do their other seats. What it does stop is this organization: its
 * workspaces, and the seat's licence key on that machine, which the app walls
 * at its next check naming who decided.
 *
 * *Free the slot* on a seat's licence is the other verb and a smaller one: it
 * makes room for another machine, and the freed one takes a slot back at its
 * next check if there is one.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color, font } from '../../theme/tokens';
import { Callout, Empty, Row, RowAction, StatusCell, Table } from '../../components/primitives';
import { useConfirm } from '../../components/Modal';
import { api, apiPath } from '../../lib/api';
import type { OrgMachines, OrgMachineSlot, OrgMachineToken, Organization } from '../../lib/types';
import { failureText } from './shared';
import { DateCell } from '../../components/DateCell';

export function OrgMachinesPage({ organization }: { organization: Organization | null }): React.JSX.Element {
  const [machines, setMachines] = useState<OrgMachines | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** A machine cut off here whose key the licence service did not get to refuse — offered *Try again*. */
  const [unsettled, setUnsettled] = useState<string | null>(null);
  const [dialog, ask] = useConfirm();
  const orgId = organization?.id ?? null;

  useEffect(() => {
    if (!orgId) return;
    void api
      .get<OrgMachines>(apiPath`/orgs/${orgId}/machines`)
      .then(setMachines)
      .catch((failure: unknown) => setError(failureText(failure)));
  }, [orgId]);

  if (!organization || !orgId) return <Empty title="No organization">Machines belong to an organization’s members.</Empty>;
  if (machines === null) return error ? <p style={{ fontSize: 13.5, color: color.badText }}>{error}</p> : <Empty title="Reading the machines…" />;

  const cutOff = async (token: OrgMachineToken, again = false): Promise<void> => {
    const name = token.label ?? 'this computer';
    const agreed = again || await ask({
      title: `Cut ${name} off from ${organization.name}?`,
      lede: `At its next check, Airlock on it locks for ${token.email}’s seat in ${organization.name} and stops reading and publishing its workspaces. The app tells them ${organization.name} cut the machine off. Their other organizations are not affected, and their files stay on its disk.`,
      confirm: 'Cut it off',
      danger: true
    });
    if (!agreed) return;
    setError(null);
    try {
      const answer = await api.post<OrgMachines & { key: string }>(apiPath`/orgs/${orgId}/machines/${token.id}/block`);
      setMachines(answer);
      setUnsettled(answer.key === 'unreachable' ? token.id : null);
      setNote(
        answer.key === 'unreachable'
          ? `${name} can no longer read ${organization.name}’s workspaces, but the licence service did not answer, so Airlock still runs there. Try again in a moment.`
          : `${name} is cut off from ${organization.name}.`
      );
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  const allow = async (token: OrgMachineToken): Promise<void> => {
    setError(null);
    try {
      setMachines(await api.del<OrgMachines>(apiPath`/orgs/${orgId}/machines/${token.id}/block`));
      setNote(`${token.label ?? 'The machine'} can use its seat in ${organization.name} again from its next check.`);
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  const release = async (slot: OrgMachineSlot): Promise<void> => {
    const agreed = await ask({
      title: `Free ${slot.label ?? 'this computer'}’s slot?`,
      lede: `It no longer counts against ${slot.email}’s seat, which makes room for another machine. It takes a slot back at its next check if one is free — to stop it, cut it off above.`,
      confirm: 'Free the slot',
      danger: true
    });
    if (!agreed) return;
    setError(null);
    try {
      setMachines(await api.del<OrgMachines>(apiPath`/orgs/${orgId}/machines/slots/${slot.seatId}/${slot.fingerprint}`));
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  const tokenColumns = '1.4fr 1.2fr 110px 110px 110px 90px';
  const slotColumns = '1.4fr 1.2fr 110px 110px 90px';

  return (
    <>
      {dialog}
      <Table
        columns={tokenColumns}
        header={['Machine', 'Member', 'Linked', 'Last used', 'Access', '']}
        toolbar={<div style={{ fontSize: 15, fontWeight: 600 }}>Linked machines</div>}
      >
        {machines.tokens.length === 0 ? (
          <Row columns={tokenColumns}>
            <span style={{ color: color.inkFaint }}>No member has linked a machine yet.</span>
          </Row>
        ) : (
          machines.tokens.map((token) => (
            <Row key={token.id} columns={tokenColumns}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{token.label ?? 'Unnamed machine'}</div>
                <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                  {token.machine}
                  {token.country ? ` · ${token.country}` : ''}
                </div>
              </div>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{token.email}</span>
              <DateCell at={token.createdAt} style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkSubtle }} />
              <DateCell at={token.lastUsedAt} style={{ fontFamily: font.mono, fontSize: 12.5 }} />
              {token.blockedAt ? (
                <StatusCell tone="warn" ring word="Cut off" />
              ) : token.revokedAt ? (
                <StatusCell tone="off" ring word="Revoked" />
              ) : (
                <StatusCell tone="ok" word="Active" />
              )}
              {token.blockedAt ? (
                <div style={{ display: 'flex', gap: 12, whiteSpace: 'nowrap' }}>
                  {unsettled === token.id && <RowAction onClick={() => void cutOff(token, true)}>Try again</RowAction>}
                  <RowAction onClick={() => void allow(token)}>Allow</RowAction>
                </div>
              ) : token.revokedAt ? (
                <span />
              ) : (
                <RowAction danger onClick={() => void cutOff(token)}>
                  Cut off
                </RowAction>
              )}
            </Row>
          ))
        )}
      </Table>

      <div style={{ height: 28 }} />
      <Table
        columns={slotColumns}
        header={['Machine', 'Member', 'First seen', 'Last seen', '']}
        toolbar={<div style={{ fontSize: 15, fontWeight: 600 }}>Running a seat’s licence</div>}
      >
        {machines.slotsProblem ? (
          <Row columns={slotColumns}>
            <span style={{ color: color.warnText }}>{machines.slotsProblem}</span>
          </Row>
        ) : machines.slots.length === 0 ? (
          <Row columns={slotColumns}>
            <span style={{ color: color.inkFaint }}>No seat’s key is running on a machine yet.</span>
          </Row>
        ) : (
          machines.slots.map((slot) => (
            <Row key={`${slot.serial}-${slot.fingerprint}`} columns={slotColumns}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{slot.label ?? 'Unnamed machine'}</div>
                <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                  {slot.fingerprint.slice(0, 12)}
                  {slot.country ? ` · ${slot.country}` : ''}
                </div>
              </div>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{slot.email}</span>
              <DateCell at={slot.firstSeen} style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkSubtle }} />
              <DateCell at={slot.lastSeen} style={{ fontFamily: font.mono, fontSize: 12.5 }} />
              <RowAction danger onClick={() => void release(slot)}>
                Free the slot
              </RowAction>
            </Row>
          ))
        )}
      </Table>

      {error && <p style={{ fontSize: 13.5, color: color.badText, margin: '16px 0 0' }}>{error}</p>}
      {note && <p style={{ fontSize: 13.5, color: color.accentText, margin: '16px 0 0' }}>{note}</p>}

      <div style={{ maxWidth: 700, marginTop: 20 }}>
        <Callout title="What cutting off does">
          At its next check, Airlock on a cut-off machine locks for the member’s seat in {organization.name} and stops
          reading its workspaces, and linking it again does not undo it. It is this organization’s decision only: the
          member’s other organizations keep working on it. Allow lifts both.
        </Callout>
      </div>
    </>
  );
}
